<template>
    <div class="whatsapp-electoral-container">
        <!-- Sidebar with conversation list -->
        <div class="conversations-sidebar">
            <div class="sidebar-header">
                <h3>📱 WhatsApp Conversations</h3>
                <div class="pagination-info">
                    Page {{ currentPage }} of {{ totalPages }}
                </div>
            </div>

            <!-- Loading state -->
            <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Loading conversations...</p>
            </div>

            <!-- Conversations list -->
            <div v-else-if="Object.keys(conversations).length > 0" class="conversations-list">
                <div 
                    v-for="(messages, whatsapp) in conversations" 
                    :key="whatsapp"
                    class="conversation-item"
                    :class="{ active: selectedWhatsApp === whatsapp }"
                    @click="selectConversation(whatsapp)"
                >
                    <div class="conversation-header">
                        <span class="whatsapp-number">{{ whatsapp }}</span>
                        <span class="message-count">{{ messages.length }} msgs</span>
                    </div>
                    <div class="last-message">
                        {{ getLastMessage(messages) }}
                    </div>
                </div>
            </div>

            <!-- Empty state -->
            <div v-else class="empty-state">
                <p>No conversations found</p>
            </div>

            <!-- Pagination controls -->
            <div class="sidebar-pagination" v-if="totalPages > 1">
                <button 
                    @click="prevPage" 
                    :disabled="currentPage === 1"
                    class="pagination-btn"
                >
                    ← Previous
                </button>
                <button 
                    @click="nextPage" 
                    :disabled="currentPage === totalPages"
                    class="pagination-btn"
                >
                    Next →
                </button>
            </div>
        </div>

        <!-- Main content area -->
        <div class="messages-content">
            <div v-if="!selectedWhatsApp" class="no-selection">
                <div class="no-selection-content">
                    <h3>Select a conversation</h3>
                    <p>Choose a WhatsApp number from the sidebar to view the conversation</p>
                </div>
            </div>

            <div v-else class="conversation-view">
                <!-- Conversation header -->
                <div class="conversation-header-bar">
                    <h3>Conversation with {{ selectedWhatsApp }}</h3>
                    <span class="message-count">{{ selectedMessages.length }} messages</span>
                </div>

                <!-- Messages -->
                <div class="messages-container">
                    <div 
                        v-for="(message, index) in selectedMessages" 
                        :key="`${selectedWhatsApp}-${index}`"
                        class="message-wrapper"
                        :class="{ 'human-message': message.role === 'human', 'assistant-message': message.role === 'assistant' }"
                    >
                        <div class="message-bubble">
                            <div class="message-role">
                                {{ message.role === 'human' ? '👤 User' : '🤖 Assistant' }}
                            </div>
                            <div class="message-text">
                                {{ message.message }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'WhatsAppElectoralMessages',
    props: ['showToast'],
    data() {
        return {
            loading: false,
            conversations: {},
            selectedWhatsApp: null,
            selectedMessages: [],
            currentPage: 1,
            pageSize: 10,
            totalConversations: 0,
            totalPages: 0
        };
    },
    computed: {
        conversationsArray() {
            return Object.keys(this.conversations);
        }
    },
    methods: {
        async fetchConversations() {
            this.loading = true;
            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_BASE_URL}/api/v2/admin-tools/electoral-conversations/`,
                    {
                        params: {
                            page: this.currentPage,
                            page_size: this.pageSize
                        },
                        withCredentials: true
                    }
                );

                this.conversations = response.data.conversations;
                this.totalConversations = response.data.pagination.total_conversations;
                this.totalPages = response.data.pagination.total_pages;

                // Reset selection if the current selection is not in the new page
                if (this.selectedWhatsApp && !this.conversations[this.selectedWhatsApp]) {
                    this.selectedWhatsApp = null;
                    this.selectedMessages = [];
                }

            } catch (error) {
                console.error('Error fetching electoral conversations:', error);
                this.$emit('show-toast', 'Error fetching conversations', 'error');
            } finally {
                this.loading = false;
            }
        },

        selectConversation(whatsapp) {
            this.selectedWhatsApp = whatsapp;
            this.selectedMessages = this.conversations[whatsapp] || [];
        },

        getLastMessage(messages) {
            if (!messages || messages.length === 0) return 'No messages';
            const lastMessage = messages[messages.length - 1];
            const text = lastMessage.message || '';
            return text.length > 50 ? text.substring(0, 50) + '...' : text;
        },

        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++;
                this.fetchConversations();
            }
        },

        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.fetchConversations();
            }
        }
    },

    mounted() {
        this.fetchConversations();
    }
};
</script>

<style scoped>
.whatsapp-electoral-container {
    display: flex;
    height: 100%;
    width: 100%;
    background-color: #f5f5f5;
    border-radius: 8px;
    overflow: hidden;
}

.conversations-sidebar {
    width: 350px;
    background-color: white;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.sidebar-header {
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
    background-color: #f8f9fa;
}

.sidebar-header h3 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 16px;
}

.pagination-info {
    font-size: 12px;
    color: #666;
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #666;
}

.loading-spinner {
    border: 2px solid #f3f3f3;
    border-top: 2px solid #a1344e;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
    margin-bottom: 10px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.conversations-list {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

.conversation-item {
    padding: 15px 20px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.conversation-item:hover {
    background-color: #f8f9fa;
}

.conversation-item.active {
    background-color: #e3f2fd;
    border-left: 4px solid #a1344e;
}

.conversation-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 5px;
}

.whatsapp-number {
    font-weight: 600;
    color: #333;
    font-size: 14px;
}

.message-count {
    font-size: 11px;
    background-color: #e0e0e0;
    color: #666;
    padding: 2px 6px;
    border-radius: 10px;
}

.last-message {
    font-size: 12px;
    color: #666;
    line-height: 1.4;
}

.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #666;
    font-style: italic;
}

.sidebar-pagination {
    padding: 15px 20px;
    border-top: 1px solid #e0e0e0;
    display: flex;
    gap: 10px;
}

.pagination-btn {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #d0d0d0;
    background-color: white;
    color: #333;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
    background-color: #f0f0f0;
    border-color: #a1344e;
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.messages-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: white;
}

.no-selection {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.no-selection-content {
    text-align: center;
    color: #666;
}

.no-selection-content h3 {
    margin-bottom: 10px;
    color: #333;
}

.conversation-view {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.conversation-header-bar {
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
    background-color: #f8f9fa;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.conversation-header-bar h3 {
    margin: 0;
    color: #333;
    font-size: 16px;
}

.conversation-header-bar .message-count {
    font-size: 12px;
    background-color: #a1344e;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
}

.messages-container {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.message-wrapper {
    display: flex;
    align-items: flex-start;
}

.message-wrapper.human-message {
    justify-content: flex-end;
}

.message-wrapper.assistant-message {
    justify-content: flex-start;
}

.message-bubble {
    max-width: 70%;
    padding: 12px 16px;
    border-radius: 12px;
    word-wrap: break-word;
}

.human-message .message-bubble {
    background-color: #a1344e;
    color: white;
}

.assistant-message .message-bubble {
    background-color: #e9ecef;
    color: #333;
}

.message-role {
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 5px;
    opacity: 0.8;
}

.message-text {
    line-height: 1.4;
    font-size: 14px;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
    .conversations-sidebar {
        width: 300px;
    }
    
    .message-bubble {
        max-width: 85%;
    }
}
</style>
